import { Col, Row } from "antd";
import cuid from "cuid";
import { FieldArray, Formik, useFormikContext } from "formik";
import { defaultsDeep, get } from "lodash";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BiPlus, BiTrash } from "react-icons/bi";
import { MdOpenInNew } from "react-icons/md";
import { useNavigate } from "react-router";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import graphql from "utils/api/graphql";
import {
  PROCESS_CONFIG_TYPES,
  PROCESS_TAGS,
  SCREENS,
  TEMPLATES,
} from "utils/constants";
import { bindArrayInputProps } from "utils/helpers/input";

// Helper component to sync form values to parent
const FormSyncEffect = ({ onProcessStatusesChange }) => {
  const { values } = useFormikContext();
  const processStatusesJson = JSON.stringify(values.processStatuses);

  useEffect(() => {
    if (onProcessStatusesChange) {
      onProcessStatusesChange(values.processStatuses);
    }
  }, [processStatusesJson, onProcessStatusesChange]);

  return null;
};

const Statuses = ({
  formRef,
  dirtyRef,
  setIsDirty,
  TABS,
  data,
  onSubmit,
  processStatuses,
  config,
  onProcessStatusesChange,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const navigate = useNavigate();
  const FORM = t("FORM", { returnObjects: true });
  const TEMPLATES_LABELS = t("TEMPLATES", { returnObjects: true });
  const TAGS = t("PROCESS_TAGS", { returnObjects: true });

  return (
    <Formik
      innerRef={(formik) => {
        formRef.current = formik;
      }}
      initialValues={{
        processStatuses: [...(processStatuses || [])]
          .map((status) => ({
            ...status,
            possibleStatuses: [...(status.possibleStatuses || [])].sort(
              (a, b) => a.order - b.order,
            ),
          }))
          .sort((a, b) => parseInt(a.order) - parseInt(b.order)),
      }}
      onSubmit={async (values) => {
        await onSubmit({
          type: data.type,
          name: data.name,
          theme: data.theme,
          description: data.description,
          skills: data.skills,
          config: data.config,
          attributionType: defaultsDeep(
            config.find(
              ({ type }) => type === PROCESS_CONFIG_TYPES.ATTRIBUTION_TYPE,
            ),
            {
              id: cuid(),
              type: PROCESS_CONFIG_TYPES.ATTRIBUTION_TYPE,
              value: null,
            },
          ),
          processStatuses: values.processStatuses,
          insurancePlans: data.insurancePlans?.map(({ id }) => id),
        });
        setIsDirty((prev) => ({ ...prev, [TABS.STATUSES]: false }));
      }}
    >
      {({
        values: statusesValues,
        setFieldValue: setStatusesFieldValue,
        dirty,
        ...statusesFormProps
      }) => {
        if (dirtyRef.current !== dirty) {
          dirtyRef.current = dirty;
          setIsDirty((prev) => ({ ...prev, [TABS.STATUSES]: dirty }));
        }

        return (
          <Card type="simple" head={false} title={FORM.STATUSES_INFO}>
            <FormSyncEffect onProcessStatusesChange={onProcessStatusesChange} />
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form>
                  <FieldArray
                    name={"processStatuses"}
                    render={(arrayHelpers) => [
                      statusesValues.processStatuses.map((_, i) => [
                        <Col lg={4} key={`name-${_.id}`}>
                          <Input
                            placeholder={FORM.STATUSES.NAME}
                            label={FORM.STATUSES.NAME}
                            {...bindArrayInputProps({
                              parent: "processStatuses",
                              index: i,
                              name: "name",
                              values: statusesValues,
                              ...statusesFormProps,
                            })}
                          />
                        </Col>,
                        <Col lg={4} key={`tag-${_.id}`}>
                          <Select
                            type="simple"
                            label={FORM.STATUSES.TAG}
                            options={Object.entries(PROCESS_TAGS).map(
                              ([key, value]) => ({
                                value,
                                label: TAGS[key],
                              }),
                            )}
                            placeholder={FORM.STATUSES.TAG}
                            {...bindArrayInputProps({
                              parent: "processStatuses",
                              index: i,
                              name: "tag",
                              values: statusesValues,
                              ...statusesFormProps,
                            })}
                          />
                        </Col>,
                        <Col lg={2} key={`progress-${_.id}`}>
                          <Input
                            placeholder={FORM.STATUSES.PROGRESS}
                            label={FORM.STATUSES.PROGRESS}
                            {...bindArrayInputProps({
                              parent: "processStatuses",
                              index: i,
                              name: "progress",
                              values: statusesValues,
                              ...statusesFormProps,
                            })}
                          />
                        </Col>,
                        <Col lg={2} key={`order-${_.id}`}>
                          <Input
                            placeholder={FORM.STATUSES.ORDER}
                            label={FORM.STATUSES.ORDER}
                            {...bindArrayInputProps({
                              parent: "processStatuses",
                              index: i,
                              name: "order",
                              values: statusesValues,
                              ...statusesFormProps,
                            })}
                          />
                        </Col>,
                        <Col lg={2} key={`template-${_.id}`}>
                          <Select
                            options={Object.entries(
                              get(
                                TEMPLATES,
                                `${data.type}.THEMES.${data.theme}`,
                                {},
                              ),
                            ).map(([key, value]) => ({
                              label: TEMPLATES_LABELS[key],
                              value,
                            }))}
                            placeholder={FORM.STATUSES.TEMPLATE}
                            label={FORM.STATUSES.TEMPLATE}
                            {...bindArrayInputProps({
                              parent: "processStatuses",
                              index: i,
                              name: "template",
                              values: statusesValues,
                              ...statusesFormProps,
                            })}
                          />
                        </Col>,
                        <Col lg={4} key={`dynamicTemplate-${_.id}`}>
                          <Select
                            type="async"
                            node={{
                              query: graphql.queries.TEMPLATES,
                              variables: {},
                              accessors: {
                                root: "templates.data",
                                label: "name",
                              },
                            }}
                            placeholder={FORM.STATUSES.DYNAMIC_TEMPLATE}
                            label={FORM.STATUSES.DYNAMIC_TEMPLATE}
                            {...bindArrayInputProps({
                              parent: "processStatuses",
                              index: i,
                              name: "dynamicTemplate.id",
                              values: statusesValues,
                              ...statusesFormProps,
                            })}
                          />
                        </Col>,
                        <Col lg={1} key={`preview-${_.id}`}>
                          <MdOpenInNew
                            onClick={() =>
                              navigate(
                                `${SCREENS.PRIVATE.ADMIN.PROCESSES.PREVIEW.path}/${_.id}`,
                                {
                                  state: {
                                    dynamicTemplateId: _.dynamicTemplate?.id,
                                  },
                                },
                              )
                            }
                            size={24}
                          />
                        </Col>,
                        <Col lg={2} key={`move-${_.id}`}>
                          <Input
                            type="checkbox"
                            placeholder={FORM.STATUSES.MOVE_TO_NEXT}
                            label={FORM.STATUSES.MOVE_TO_NEXT}
                            {...bindArrayInputProps({
                              parent: "processStatuses",
                              index: i,
                              name: "moveToNext",
                              values: statusesValues,
                              ...statusesFormProps,
                            })}
                            checked={
                              statusesValues.processStatuses[i].moveToNext
                            }
                          />
                        </Col>,
                        <Col lg={1} key={`delete-${_.id}`}>
                          <div
                            className={"delete-icon"}
                            onClick={() => {
                              arrayHelpers.remove(i);
                            }}
                          >
                            <BiTrash color={COLORS.C_DANGER} size={24} />
                          </div>
                        </Col>,
                      ]),
                      <Button
                        key="add-row"
                        onClick={() => {
                          arrayHelpers.push({ id: cuid() });
                        }}
                        type="primary"
                      >
                        <BiPlus size={24} />
                      </Button>,
                    ]}
                  />
                </Form>
              </Col>
            </Row>
          </Card>
        );
      }}
    </Formik>
  );
};

export default Statuses;
